import { createSlice } from '@reduxjs/toolkit';
import { matchPath } from 'react-router-dom';

import { SurveyServiceClient } from '../../common/services/httpClient';
import {
  fetchDivestitureDiagnosticResultsPath,
  resetDivestitureDiagnosticResultsPath,
  saveCategoriesPath,
  saveOverallComplexityPath,
} from '../../common/services/urlService';
import httpAction from '../../Survey/httpAction';
import { contentResponsesRoute } from '../../Survey/routes';

export const DD_REDUCER = 'divestitureDiagnostic/';

export const coerceDivestitureDiagnosticResults = (
  divestitureDiagnosticResults
) =>
  Object.keys(divestitureDiagnosticResults).length
    ? divestitureDiagnosticResults
    : null;

export const initialState = {
  fetchDivestitureDiagnosticResultsStatus: {
    pending: false,
    success: false,
    error: false,
  },
  resetDivestitureDiagnosticResultsStatus: {
    pending: false,
    success: false,
    error: false,
  },
  saveCategoriesStatus: {
    pending: false,
    success: false,
    error: false,
  },
  saveOverallComplexityStatus: {
    pending: false,
    success: false,
    error: false,
  },
  divestitureDiagnosticResults: null,
  editMode: false,
};

export const reducers = {
  resetDivestitureDiagnostic: () => initialState,
  fetchDivestitureDiagnosticResultsBegan: (state) => {
    return {
      ...state,
      fetchDivestitureDiagnosticResultsStatus: {
        pending: true,
        success: false,
        error: false,
      },
    };
  },
  fetchDivestitureDiagnosticResultsSuccess: (
    state,
    { payload: divestitureDiagnosticResults }
  ) => {
    return {
      ...state,
      fetchDivestitureDiagnosticResultsStatus: {
        pending: false,
        success: true,
        error: false,
      },
      divestitureDiagnosticResults: coerceDivestitureDiagnosticResults(
        divestitureDiagnosticResults
      ),
    };
  },
  fetchDivestitureDiagnosticResultsError: (state, { payload: error }) => {
    return {
      ...state,
      fetchDivestitureDiagnosticResultsStatus: {
        pending: false,
        success: false,
        error,
      },
    };
  },
  resetDivestitureDiagnosticResultsBegan: (state) => {
    return {
      ...state,
      resetDivestitureDiagnosticResultsStatus: {
        pending: true,
        success: false,
        error: false,
      },
    };
  },
  resetDivestitureDiagnosticResultsSuccess: (state) => {
    return {
      ...state,
      resetDivestitureDiagnosticResultsStatus: {
        pending: false,
        success: true,
        error: false,
      },
      divestitureDiagnosticResults: null,
    };
  },
  resetDivestitureDiagnosticResultsError: (state, { payload: error }) => {
    return {
      ...state,
      resetDivestitureDiagnosticResultsStatus: {
        pending: false,
        success: false,
        error,
      },
    };
  },
  saveCategoriesBegan: (state) => {
    return {
      ...state,
      saveCategoriesStatus: {
        pending: true,
        success: false,
        error: false,
      },
    };
  },
  saveCategoriesSuccess: (state) => {
    return {
      ...state,
      saveCategoriesStatus: {
        pending: false,
        success: true,
        error: false,
      },
    };
  },
  saveCategoriesError: (state, { payload: error }) => {
    return {
      ...state,
      saveCategoriesStatus: {
        pending: false,
        success: false,
        error,
      },
    };
  },
  saveOverallComplexityBegan: (state) => {
    return {
      ...state,
      saveOverallComplexityStatus: {
        pending: true,
        success: false,
        error: false,
      },
    };
  },
  saveOverallComplexitySuccess: (state) => {
    return {
      ...state,
      saveOverallComplexityStatus: {
        pending: false,
        success: true,
        error: false,
      },
    };
  },
  saveOverallComplexityError: (state, { payload: error }) => {
    return {
      ...state,
      saveOverallComplexityStatus: {
        pending: false,
        success: false,
        error,
      },
    };
  },
  setEditMode: (state, action) => {
    return {
      ...state,
      editMode: action.payload,
    };
  },
  resetSaveCategoriesStatus: (state) => {
    return {
      ...state,
      saveCategoriesStatus: {
        pending: false,
        success: false,
        error: false,
      },
    };
  },
  resetSaveOverallComplexityStatus: (state) => {
    return {
      ...state,
      saveOverallComplexityStatus: {
        pending: false,
        success: false,
        error: false,
      },
    };
  },
};

export const setupDivestitureDiagnosticSlice = () => {
  const divestitureDiagnosticSlice = createSlice({
    name: 'divestitureDiagnostic',
    initialState,
    reducers,
  });
  return divestitureDiagnosticSlice;
};

const { reducer, actions } = setupDivestitureDiagnosticSlice();

// httpActions
export const fetchDivestitureDiagnosticResults = (projectId) =>
  httpAction({
    reducer: `${DD_REDUCER}fetchDivestitureDiagnosticResults`,
    endpoint: fetchDivestitureDiagnosticResultsPath(projectId),
    verb: 'GET',
    httpClient: SurveyServiceClient(projectId),
  });

export const resetDivestitureDiagnosticResults = (projectId) =>
  httpAction({
    reducer: `${DD_REDUCER}resetDivestitureDiagnosticResults`,
    endpoint: resetDivestitureDiagnosticResultsPath(projectId),
    verb: 'DELETE',
    httpClient: SurveyServiceClient(projectId),
  });

export const saveCategories = (projectId, subcategories) =>
  httpAction({
    reducer: `${DD_REDUCER}saveCategories`,
    endpoint: saveCategoriesPath(projectId),
    verb: 'PUT',
    httpClient: SurveyServiceClient(projectId),
    body: subcategories,
  });

export const saveOverallComplexity = (projectId, overallComplexity) =>
  httpAction({
    reducer: `${DD_REDUCER}saveOverallComplexity`,
    endpoint: saveOverallComplexityPath(projectId),
    verb: 'PUT',
    httpClient: SurveyServiceClient(projectId),
    body: overallComplexity,
  });

// Actions
export const {
  setEditMode,
  resetDivestitureDiagnostic,
  resetSaveCategoriesStatus,
  resetSaveOverallComplexityStatus,
} = actions;

// Selectors
export const getFetchDivestitureDiagnosticResultsStatus = (state) =>
  state.divestitureDiagnostic.fetchDivestitureDiagnosticResultsStatus;
export const getDivestitureDiagnosticResults = (state) =>
  state.divestitureDiagnostic.divestitureDiagnosticResults;
export const getResetDivestitureDiagnosticResultsStatus = (state) =>
  state.divestitureDiagnostic.resetDivestitureDiagnosticResultsStatus;
export const getEditMode = (state) => state.divestitureDiagnostic.editMode;
export const getSaveCategoriesStatus = (state) =>
  state.divestitureDiagnostic.saveCategoriesStatus;
export const getSaveOverallComplexityStatus = (state) =>
  state.divestitureDiagnostic.saveOverallComplexityStatus;
export const getCurrentContentTypeFromURL = (state) => {
  const { pathname } = state.router.location;
  const { params } = matchPath(pathname, {
    path: contentResponsesRoute,
  });
  return params.content;
};

export default reducer;
